import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Check, ShoppingCart, Beaker, Leaf, Award, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: Beaker, title: '102 Minerals', description: 'Comprehensive mineral complex for optimal health' },
  { icon: Leaf, title: '9 Amino Acids', description: 'Essential building blocks for your body' },
  { icon: Award, title: 'OSU Developed', description: 'Created as a capstone project at Oregon State University' },
  { icon: Heart, title: 'Daily Health', description: 'One delicious serving per day for wellness' },
];

const aminoAcids = [
  'L-Leucine', 'L-Isoleucine', 'L-Valine', 'L-Lysine', 'L-Methionine',
  'L-Phenylalanine', 'L-Threonine', 'L-Tryptophan', 'L-Histidine'
];

export default function CocoaLifePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?category=supplement`);
        setProducts(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div className="min-h-screen" data-testid="cocoalife-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/10 via-background to-background" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-accent/10 rounded-full blur-[200px]" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Badge className="badge-accent mb-6">Oregon State University Capstone Project</Badge>
              
              <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-none mb-6" style={{ fontFamily: 'Playfair Display' }}>
                Cocoa<br />
                <span className="text-accent">Life™</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-lg">
                A revolutionary chocolate truffle infused with <strong className="text-foreground">102 minerals</strong> and <strong className="text-foreground">9 amino acid complexes</strong>. 
                Your daily delicious path to wellness.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border">
                  <Check className="w-4 h-4 text-accent" />
                  <span className="text-sm font-medium">OSU Developed</span>
                </div>
                <div className="flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border">
                  <Check className="w-4 h-4 text-accent" />
                  <span className="text-sm font-medium">Premium Chocolate</span>
                </div>
                <div className="flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border">
                  <Check className="w-4 h-4 text-accent" />
                  <span className="text-sm font-medium">One-a-Day</span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-4xl font-black text-secondary">$79.99</span>
                <span className="text-muted-foreground">/ 30-day supply</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl overflow-hidden border border-accent/30" style={{ boxShadow: '0 0 60px rgba(244, 196, 48, 0.2)' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/c4m7cv1f_Firefly_a%20beautify%20table%20with%20a%20fancy%20pl%2027140%2056A.jpg"
                  alt="Super Truffle Chocolate"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Floating badges */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute -top-4 -right-4 bg-accent text-accent-foreground px-4 py-2 rounded-full font-bold shadow-lg"
              >
                102 Minerals
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="absolute -bottom-4 -left-4 bg-secondary text-white px-4 py-2 rounded-full font-bold shadow-lg"
              >
                9 Amino Complexes
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              The Story Behind CocoaLife™
            </h2>
            <p className="text-muted-foreground">Watch the mission statement, commercial, and discovery</p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="video-wrapper glow-orange">
              <iframe
                src="https://www.youtube.com/embed/dIUBN4EcA_o"
                title="Super Truffle - Mission Statement & Discovery"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-header mb-4">Why Super Truffle?</h2>
            <p className="section-subheader max-w-2xl mx-auto">
              Developed through rigorous research at Oregon State University, 
              Super Truffle delivers comprehensive nutrition in one daily serving.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-card border-border/50 h-full text-center card-hover">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-accent/20 flex items-center justify-center mb-4">
                      <benefit.icon className="w-8 h-8 text-accent" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ingredients Section */}
      <section className="py-24 md:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="section-header mb-6">9 Essential Amino Acids</h2>
              <p className="text-muted-foreground mb-8">
                Each serving contains all nine essential amino acids your body cannot produce on its own. 
                These building blocks support muscle growth, immune function, and overall vitality.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {aminoAcids.map((acid, index) => (
                  <motion.div
                    key={acid}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-2 bg-muted/50 px-4 py-3 rounded-xl"
                  >
                    <Check className="w-4 h-4 text-accent flex-shrink-0" />
                    <span className="text-sm font-medium">{acid}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-card border-accent/30 overflow-hidden" style={{ boxShadow: '0 0 40px rgba(244, 196, 48, 0.1)' }}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">102 Trace Minerals</h3>
                      <p className="text-muted-foreground text-sm">Complete mineral spectrum</p>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-6">
                    Our proprietary mineral complex includes over 100 trace minerals sourced from 
                    natural deposits, providing comprehensive nutritional support.
                  </p>

                  <Separator className="my-6" />

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Organic Truffle Extract</span>
                      <span className="font-medium">Primary Base</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Mineral Complex</span>
                      <span className="font-medium">102 Minerals</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amino Acid Blend</span>
                      <span className="font-medium">9 Essential</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-header mb-4">Choose Your Supply</h2>
            <p className="section-subheader">Start your wellness journey today</p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[1, 2].map(i => (
                <div key={i} className="aspect-[4/3] skeleton rounded-3xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`overflow-hidden h-full ${index === 1 ? 'border-accent' : 'border-border/50'}`} style={index === 1 ? { boxShadow: '0 0 40px rgba(244, 196, 48, 0.15)' } : {}}>
                    {index === 1 && (
                      <div className="bg-accent text-accent-foreground text-center py-2 text-sm font-bold">
                        BEST VALUE - Save $40
                      </div>
                    )}
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-2xl font-bold mb-2">{product.name}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{product.description}</p>
                      
                      <div className="flex items-end justify-between mb-6">
                        <div>
                          <p className="text-3xl font-black text-secondary">${product.price.toFixed(2)}</p>
                          {product.details?.servings && (
                            <p className="text-sm text-muted-foreground">{product.details.servings}</p>
                          )}
                        </div>
                      </div>

                      <Button
                        className={`w-full h-14 text-lg ${index === 1 ? 'bg-accent text-accent-foreground hover:bg-accent/90' : 'btn-primary'}`}
                        onClick={() => handleAddToCart(product)}
                        data-testid={`add-truffle-${product.id}`}
                      >
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Add to Cart
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-background" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-header mb-6">Ready to Transform Your Health?</h2>
            <p className="section-subheader max-w-2xl mx-auto mb-8">
              Join thousands who have made Super Truffle part of their daily wellness routine.
            </p>
            <Link to="/shop">
              <Button className="btn-primary h-16 px-12 text-xl" data-testid="truffle-shop-cta">
                <Sparkles className="w-6 h-6 mr-2" />
                Shop Now
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
